/**
 * Контракт ключей движковых системных сообщений (i18n ядра).
 *
 * Источник — `packages/core/src/engine/system-messages.ts` (en-шаблоны). Здесь живёт
 * только КОНТРАКТ: статический список ключей и наборов {param}-плейсхолдеров, без
 * текста. Текст en остаётся в core, ru — в ui; `@itphx/synth-shared` потребляют обе
 * стороны для автопроверки синхронизации.
 *
 * - `ENGINE_MESSAGE_KEYS` — полный список движковых ключей (должен совпадать с
 *   keys(SYSTEM_MESSAGES) в core).
 * - `ENGINE_MESSAGE_PARAMS` — для каждого ключа имена {param}-плейсхолдеров en-шаблона.
 * - `extractParams` — утилита извлечения плейсхолдеров (без логики движка).
 */
export const ENGINE_MESSAGE_KEYS = [
    'session.max_iterations',
    'session.steps_limit',
    'session.stopped',
    'session.stopped_reason_denied',
    'session.denied_blocked',
    'session.role_tool_blocked',
    'session.empty_response',
    'session.output_limit',
    'session.default_title',
    'session.reference_label',
    'session.reference_link',
    'session.retry_cancelled',
    'session.subtask_title',
    'task.stopped_by_user',
    'task.stopped_before_completion',
    'task.stopped_by_deny',
    'task.empty_response',
    'task.subagent_exec_error',
    'task.subagent_not_found',
    'task.subagent_not_allowed',
    'task.deps_unavailable',
    'task.model_unavailable',
    'task.model_not_in_catalog',
    'task.subagent_exec_error_after_retries',
    'task.depth_limit',
    'task.resume_marker',
    'task.resume_result',
    'task.resume_failed',
    'task.resume_aborted',
    'task.resume_session_not_found',
    'task.resume_no_parent',
    'task.resume_parent_not_found',
    'task.resume_locked',
    'task.resume_agent_not_found',
    'task.resume_provider_unavailable',
    'task.steps_limit',
    'task.steps_limit_empty',
    'task.context_budget',
    'subagent.context_budget',
    'task.background_started',
    'task.background_limit',
    'task.background_disabled',
    'task.resume_foreign_child',
    'task.resume_child_busy',
    'tool.args_error',
    'tool.exec_error',
    // Вызов инструмента без имени/идентификатора — результат всё равно должен
    // быть записан парным `tool`-сообщением (иначе у провайдера рвётся цепочка).
    'tool.malformed_call',
    'tool.not_found',
    'tool.permission_denied_default',
    'tool.doom_loop',
    'tool.disabled_by_step_limit',
    'tool.empty_output',
    'tool.output_truncated',
    // Общий предел вывода инструментов: результат усечён по строкам/байтам.
    'tool.output_bounded',
    'tool.shell_error',
    // ADR-113 §detach: сообщение о таймауте detach-готового foreground-пути.
    'tool.shell_timeout',
    // Таймаут поиска (`grep_files`/`glob`) — actionable-текст с подсказкой.
    'tool.search_timeout',
    'tool.command_failed_default',
    'tool.aborted',
    'tool.binary_missing',
    'fs.file_written',
    'fs.file_edited',
    'fs.replace_not_found',
    'fs.replace_multiple',
    // Отказы до записи: идентичные строки, пустой old_string, смешанные переводы строк.
    'fs.no_changes',
    'fs.empty_old_string',
    'fs.mixed_line_endings',
    // Защита от бинаря и гигантских файлов.
    'fs.edit_binary',
    'fs.write_binary',
    'fs.file_too_large',
    'fs.file_not_found',
    'fs.dir_created',
    'fs.file_deleted',
    // Мультифайловая атомарная правка (`patch`, JSON-edits).
    'fs.patch_applied',
    'fs.patch_file_line',
    'fs.patch_not_found',
    'fs.patch_multiple',
    'fs.patch_write_failed',
    'fs.patch_file_missing',
    'fs.patch_not_regular',
    'fs.patch_too_large',
    'fs.patch_binary',
    // Операции патча add/delete/rename (сводка + строки по файлам).
    'fs.patch_summary',
    'fs.patch_file_added',
    'fs.patch_file_deleted',
    'fs.patch_file_renamed',
    'fs.patch_add_exists',
    'fs.patch_delete_missing',
    'fs.patch_rename_missing',
    'fs.patch_rename_exists',
    'fs.patch_path_escapes',
    'fsread.empty_directory',
    'fsread.empty_file',
    'fsread.no_files',
    'fsread.glob_error',
    'fsread.truncated',
    'fsread.directory_truncated',
    'fsread.line_truncated',
    'fsread.offset_out_of_range',
    'fsread.binary',
    'fsread.glob_truncated',
    'web.duckduckgo_blocked',
    'web.search_failed',
    'web.search_results',
    'web.fetch_args_url_required',
    'web.js_framework_warning',
    'web.low_content_warning',
    'web.content_truncated',
    'web.body_truncated',
    'web.block_timeout',
    'web.block_error',
    'web.fetch_all_failed',
    'web.no_results',
    'web.search_error',
    'web.search_rate_limited',
    'web.search_auth_failed',
    'web.ssrf_blocked',
    'web.too_many_redirects',
    'search.no_matches',
    'search.matches_truncated',
    'search.rg_not_installed',
    'search.error',
    // Различимые причины отказа поиска (ADR-041): путь не найден / путь не
    // каталог / невалидный regex — у каждой своя подсказка модели.
    'search.path_not_found',
    'search.path_not_directory',
    'search.invalid_regex',
    'plan.enter_success',
    'plan.exit_success',
    'plan.session_manager_unavailable',
    'plan.enter_failed',
    'plan.exit_failed',
    'plan.project_store_unavailable',
    'plan.no_project',
    'plan.no_project_read',
    'plan.column_not_found',
    'plan.item_not_found',
    'plan.save_error',
    'plan.markdown_title',
    'plan.markdown_empty',
    'plan.markdown_column_empty',
    'plan.markdown_dependencies',
    'plan.default_column_backlog',
    'plan.default_column_todo',
    'plan.default_column_in_progress',
    'plan.default_column_done',
    'todo.save_error',
    'todo.read_error',
    'todo.list_empty',
    'todo.list',
    'todo.item_not_found',
    'todo.items_not_found',
    'todo.status_pending',
    'todo.status_in_progress',
    'todo.status_completed',
    'todo.status_cancelled',
    'todo.priority_high',
    'todo.priority_medium',
    'todo.priority_low',
    // Компактный вывод тулов todo (по образцу opencode): сводка + строки пунктов.
    'todo.summary',
    'todo.line',
    'todo.more',
    'todo.added',
    'todo.updated',
    'todo.deleted',
    'todo.hint_priority',
    'todo.hint_plan',
    'project.deps_unavailable',
    'project.session_read_error',
    'project.session_not_found',
    'project.save_error',
    'project.workspace_create_error',
    'project.created',
    'project.read_error',
    'project.not_found',
    'project.attached',
    'project.log_no_project',
    'project.log_save_error',
    'project.log_added',
    'project.log_empty',
    'project.log_list',
    'project.invariants_no_project',
    'project.invariants_empty',
    'project.invariants_saved',
    'project.invariants_save_error',
    'project.write_forbidden',
    'project.updated',
    'project.updated_ok',
    'project.invalid_icon',
    'project.invalid_color',
    'project.no_project',
    'project.workspace_set',
    'project.workspace_invalid',
    'project.workspace_denied',
    'project.workspace_confirm_required',
    'project.workspace_missing',
    'project.repo_cloned',
    'project.repo_initialized',
    'project.repo_registered',
    'project.repo_fetched',
    'project.repo_removed',
    'project.repo_not_found',
    'projectctx.info_title',
    'projectctx.info_id',
    'projectctx.info_workspace',
    'projectctx.info_description',
    'projectctx.not_found',
    'projectctx.missing',
    'projectctx.working_dir',
    'projectctx.no_plan_hint',
    'projectctx.git_section',
    'projectctx.repo_branch',
    'projectctx.repo_remote',
    'projectctx.repo_state',
    'projectctx.repo_state_dirty',
    'projectctx.repo_state_clean',
    'projectctx.repo_last_commit',
    'projectctx.repos_title',
    'projectctx.repo_not_found',
    'projectctx.repos_none',
    'projectctx.repos_found',
    'projectctx.repo_destination',
    'projectctx.repo_name',
    'projectctx.repo_url',
    'projectctx.repo_type',
    'projectctx.repo_default_branch',
    'projectctx.repo_ssh_key',
    'projectctx.repo_last_fetch',
    'tool.classified',
    'reference.session_manager_unavailable',
    'reference.session_not_found',
    'reference.no_messages',
    'reference.session_title',
    'permission.yolo_auto_approved',
    'permission.plan_write_blocked',
    'permission.plan_source_mode',
    'permission.plan_source_strict',
    'permission.plan_shell_denied',
    'permission.mcp_unavailable',
    'permission.dep_unavailable',
    'permission.workspace_unavailable',
    'permission.storage_unavailable',
    'permission.external_directory_default',
    'permission.no_rules',
    'permission.rule_matched',
    'permission.rule_pattern_matched',
    'permission.rule_prefix_matched',
    'permission.rule_command_matched',
    'permission.ask_timeout',
    'permission.ask_cancelled',
    'permission.ask_no_subscribers',
    'permission.ask_denied',
    'permission.authority_denied_unknown',
    'permission.authority_denied_explicit',
    'permission.authority_denied_no_grant',
    'permission.authority_ask',
    'shellguard.allow_default',
    'shellguard.deny_prefix',
    'shellguard.deny_path',
    'shellguard.ask_path',
    'shellguard.deny_dir',
    'protectedpaths.unresolvable',
    'protectedpaths.dynamic',
    'protectedpaths.allow_default',
    'security.warn_prefix',
    'security.injection_blocked',
    'gateway.input_blocked',
    'gateway.rate_limit_warning',
    'gateway.output_secrets_reason',
    'gateway.filter_denied',
    'gateway.filter_unavailable',
    'router.timeout',
    'router.network_error',
    'router.provider_not_found',
    'router.provider_disabled',
    'router.provider_unspecified',
    'router.model_disabled',
    'router.escalation',
    'router.context_length_exceeded',
    'router.rate_limit_exceeded',
    'router.no_user_query',
    'qc.min_length_ok',
    'qc.min_length_fail_detail',
    'qc.min_length_fail',
    'qc.constraint_empty',
    'qc.constraint_parse_error',
    'qc.constraint_ok',
    'qc.self_check_skipped',
    'qc.self_check_unparsed',
    'qc.self_check_low',
    'qc.self_check_low_reason',
    'qc.self_check_ok',
    'qc.self_check_error',
    'qc.all_passed',
    'micro.greeting',
    'micro.farewell',
    'micro.thanks',
    'micro.enum.yes',
    'micro.enum.no',
    'micro.enum.approve',
    'micro.enum.reject',
    'micro.enum.mode_plan',
    'micro.enum.mode_act',
    'micro.intent.question',
    'micro.intent.command',
    'micro.intent.review',
    'micro.intent.explain',
    'micro.intent.debug',
    'micro.intent.fix',
    'micro.intent.test',
    // --- Скиллы (ADR-046) ---
    'skill.args_error',
    'skill.not_found',
    'skill.untrusted',
    'skill.delegation',
    'skill.file_not_found',
    'skill.scope_disabled',
    // --- Slash-команды (ADR-046 §7) ---
    'command.unknown',
    'command.denied',
    'command.unavailable',
    'command.help',
    'command.list_empty',
    'command.skills_empty',
    'command.summary_enqueued',
    'command.summary_unavailable',
    // --- Сжатие контекста (summarization / compaction) ---
    'context.compression_empty',
    'context.compression_failed',
    'context.compression_template',
    'context.compression_output_limit',
    'context.summary_applied',
    // --- Inline shell (ADR-046 §7.4) ---
    'command.inline_shell_disabled',
    'command.inline_shell_denied',
    'command.inline_shell_failed',
    'command.inline_shell_truncated',
    'command.inline_shell_unavailable',
    // --- Git-репозитории и SSH-ключи (ADR-076) ---
    'sshkey.not_found',
    'sshkey.name_exists',
    'sshkey.encryption_unavailable',
    'sshkey.decrypt_failed',
    'sshkey.env_failed',
    'git.ssh_key_required',
    'git.ssh_key_required_hint',
    'git.ssh_keys_none',
    'sshkeys.tool.header',
    'sshkeys.tool.empty',
    'sshkeys.tool.deps_unavailable',
    'git.repo_path_invalid',
    'git.repo_not_a_repo',
    'git.repo_exists',
    'git.repo_occupied_repo',
    'git.repo_occupied_stub',
    'git.repo_occupied_dir',
    'git.repo_path_unreadable',
    'git.clone_timeout',
    'git.fetch_timeout',
    // Таймаут git-тула агента: величина лимита + что делать дальше.
    'git.timeout',
    // --- Git worktrees: per-session рабочий каталог (ADR-114) ---
    'worktree.directory_missing',
    'worktree.tool.deps_unavailable',
    'worktree.tool.no_project',
    'worktree.tool.list_empty',
    'worktree.tool.list_header',
    'worktree.tool.list_row',
    'worktree.tool.created',
    'worktree.tool.removed',
    'worktree.tool.pruned',
    'worktree.tool.force_required',
    'worktree.tool.busy',
    'worktree.tool.not_found',
    // --- Внешние события и хуки (ADR-030 / ADR-089) ---
    'event.ingested',
    'event.routed.bound',
    'event.routed.ephemeral',
    'event.routed.queue',
    'event.routed.drop',
    'event.binding.not_found',
    'event.source_not_allowed',
    'event.duplicate',
    'hook.blocked',
    // --- Steering: форсированная отправка во время хода (ADR-104) ---
    'steer.injected',
    'steer.limit_exceeded',
    'steer.interrupted',
    'steer.dropped_aborted',
    'steer.dropped_turn_ended',
    'steer.requeued_aborted',
    'steer.requeued_turn_ended',
    // --- Профиль пользователя и онбординг ---
    'profile.deps_unavailable',
    'profile.session_read_error',
    'profile.session_not_found',
    'profile.user_unavailable',
    'profile.save_error',
    'profile.updated',
    // --- Движок пайплайнов (ADR-101) ---
    'pipeline.not_found',
    'pipeline.not_published',
    'pipeline.invalid_spec',
    'pipeline.loop_limit',
    'pipeline.cancelled',
    'pipeline.executor_missing',
    'pipeline.gate_rejected',
    'pipeline.gate_timeout',
    'pipeline.step_failed',
    'pipeline.step_timeout',
    'pipeline.run_not_found',
    'pipeline.invalid_state',
    'pipeline.tool_not_found',
    'pipeline.tool_failed',
    'pipeline.script_failed',
    'pipeline.script_no_command',
    'pipeline.permission_denied',
    // --- Исполнитель agent-шага (ADR-101 §4, Э4.3) ---
    'pipeline.agent_unavailable',
    'pipeline.provider_not_found',
    'pipeline.llm_failed',
    'pipeline.llm_unrecoverable',
    // --- Встроенные тулы пайплайнов (ADR-101) ---
    'pipeline.tool.deps_unavailable',
    'pipeline.tool.no_project',
    'pipeline.tool.forbidden',
    'pipeline.tool.not_found',
    'pipeline.tool.already_running',
    'pipeline.tool.invalid_spec',
    'pipeline.tool.not_published',
    'pipeline.tool.run_not_found',
    'pipeline.tool.run_foreign',
    'pipeline.tool.run_not_waiting',
    'pipeline.tool.template_not_found',
    'pipeline.tool.template_unsupported',
    'pipeline.tool.saved',
    'pipeline.tool.published',
    'pipeline.tool.deleted',
    'pipeline.tool.run_started',
    'pipeline.tool.run_resumed',
    'pipeline.tool.run_cancelled',
    'pipeline.tool.instantiated',
    'pipeline.tool.list_empty',
    'pipeline.tool.runs_empty',
    'pipeline.tool.templates_empty',
    // --- Встроенные тулы расписаний (ADR-051) ---
    'schedule.tool.deps_unavailable',
    'schedule.tool.forbidden',
    'schedule.tool.not_found',
    'schedule.tool.invalid',
    'schedule.tool.saved',
    'schedule.tool.deleted',
    'schedule.tool.ran',
    'schedule.tool.list_empty',
    'schedule.tool.jobs_empty',
    'schedule.tool.target_forbidden',
    'schedule.tool.not_published',
    // --- Исполнение (ADR-077) ---
    'execution.remote_url_required',
    'execution.remote_unavailable',
    // --- Вопрос пользователю и фоновые процессы (этап 1 roadmap 0.13) ---
    'question.answered',
    'question.cancelled',
    'question.timeout',
    'question.deps_unavailable',
    'process.background_started',
    'process.deps_unavailable',
    'process.background_limit',
    'process.background_unsupported',
    'process.background_disabled',
    // ADR-113 §detach: идущая команда переведена в фон (без перезапуска).
    'process.detached_started',
    'process.tool.empty',
    'process.tool.not_found',
    'process.tool.deps_unavailable',
    'process.tool.header',
    'process.tool.no_log',
    'process.tool.child_history',
    // ADR-111: управление фоновыми прогонами из агента.
    'process.tool.not_running',
    'process.tool.running',
    'process.tool.stopping',
    'process.tool.deleted',
    // --- Повторная привязка файла из workspace (ADR-115) ---
    'attach.message.from_workspace',
    'attach.tool.attached',
    'attach.tool.deps_unavailable',
    'attach.tool.delivery_unavailable',
    'attach.tool.disabled',
    'attach.tool.not_found',
    'attach.tool.not_a_file',
    'attach.tool.too_large',
    'attach.tool.unsupported_mime',
    'attach.tool.modality_unsupported',
    'attach.tool.unrecognized',
    // --- Отправка файла в ответе (ADR-115): тул `send_attachment` ---
    'attach.tool.queued',
    // --- Сохранение вложения в workspace (ADR-115): тул `save_attachment` ---
    'attach.tool.saved',
    'attach.tool.ambiguous',
    'attach.tool.no_attachment',
    'attach.tool.exists',
    'attach.tool.save_failed',
    // --- Каталог моделей для агента (ADR-115): тул `list_models` ---
    'models.list.header',
    'models.list.current',
    'models.list.defaults',
    'models.list.skipped',
    'models.list.empty',
    'models.list.provider_unknown',
    'models.list.deps_unavailable',
    'models.list.unavailable',
];
export const ENGINE_MESSAGE_PARAMS = {
    'session.max_iterations': [],
    'session.steps_limit': ["limit"],
    'session.stopped': ["reason"],
    'session.stopped_reason_denied': [],
    'session.denied_blocked': [],
    'session.role_tool_blocked': ["tool", "agent"],
    'session.empty_response': [],
    'session.output_limit': ["limit", "reasoning"],
    'session.default_title': [],
    'session.reference_label': [],
    'session.reference_link': ["label", "id", "from", "to"],
    'session.retry_cancelled': [],
    'session.subtask_title': ["description"],
    'task.stopped_by_user': [],
    'task.stopped_before_completion': [],
    'task.stopped_by_deny': [],
    'task.empty_response': [],
    'task.subagent_exec_error': ["type", "error"],
    'task.subagent_not_found': ["type", "types"],
    'task.subagent_not_allowed': ["type", "modes"],
    'task.deps_unavailable': [],
    'task.model_unavailable': ["provider", "model"],
    'task.model_not_in_catalog': ["provider", "model", "available"],
    'task.subagent_exec_error_after_retries': ["type", "attempts", "error"],
    'task.depth_limit': ["limit"],
    'task.resume_marker': [],
    'task.resume_result': ["type", "summary"],
    'task.resume_failed': ["type", "error"],
    'task.resume_aborted': ["type"],
    'task.resume_session_not_found': ["id"],
    'task.resume_no_parent': ["id"],
    'task.resume_parent_not_found': ["id"],
    'task.resume_locked': ["id"],
    'task.resume_agent_not_found': ["type"],
    'task.resume_provider_unavailable': ["id"],
    'task.steps_limit': ["limit"],
    'task.steps_limit_empty': ["limit"],
    'task.context_budget': ["tokens", "steps", "tasks"],
    'subagent.context_budget': ["tokens", "steps"],
    'task.background_started': ["id"],
    'task.background_limit': ["limit"],
    'task.background_disabled': [],
    'task.resume_foreign_child': ["id"],
    'task.resume_child_busy': ["id"],
    'tool.args_error': ["tool", "issues"],
    'tool.exec_error': ["tool", "error"],
    'tool.malformed_call': ["tool", "id"],
    'tool.not_found': ["tool"],
    'tool.permission_denied_default': [],
    'tool.doom_loop': ["tool", "count"],
    'tool.disabled_by_step_limit': ["tool", "limit"],
    'tool.empty_output': [],
    'tool.output_truncated': [],
    'tool.output_bounded': ["shown", "total"],
    'tool.shell_error': ["message"],
    'tool.shell_timeout': ["timeout"],
    'tool.search_timeout': ["seconds"],
    'tool.command_failed_default': [],
    'tool.aborted': [],
    'tool.binary_missing': ["tool", "binary"],
    'fs.file_written': ["path", "lines", "bytes"],
    'fs.file_edited': ["path", "count"],
    'fs.replace_not_found': ["path"],
    'fs.replace_multiple': ["count"],
    'fs.no_changes': [],
    'fs.empty_old_string': [],
    'fs.mixed_line_endings': [],
    'fs.edit_binary': ["path"],
    'fs.write_binary': ["path"],
    'fs.file_too_large': ["limit"],
    'fs.file_not_found': ["path"],
    'fs.dir_created': ["path"],
    'fs.file_deleted': ["path"],
    'fs.patch_applied': ["files", "replacements"],
    'fs.patch_file_line': ["path", "count"],
    'fs.patch_not_found': ["path", "index"],
    'fs.patch_multiple': ["path", "index", "count"],
    'fs.patch_write_failed': ["path", "error"],
    'fs.patch_file_missing': ["path"],
    'fs.patch_not_regular': ["path"],
    'fs.patch_too_large': ["path", "bytes", "limit"],
    'fs.patch_binary': ["path"],
    'fs.patch_summary': ["added", "updated", "deleted", "renamed"],
    'fs.patch_file_added': ["path"],
    'fs.patch_file_deleted': ["path"],
    'fs.patch_file_renamed': ["from", "to"],
    'fs.patch_add_exists': ["path"],
    'fs.patch_delete_missing': ["path"],
    'fs.patch_rename_missing': ["from"],
    'fs.patch_rename_exists': ["to"],
    'fs.patch_path_escapes': ["path"],
    'fsread.empty_directory': [],
    'fsread.empty_file': [],
    'fsread.no_files': [],
    'fsread.glob_error': [],
    'fsread.truncated': ["count", "next"],
    'fsread.directory_truncated': ["count", "next"],
    'fsread.line_truncated': [],
    'fsread.offset_out_of_range': ["offset", "total"],
    'fsread.binary': ["path"],
    'fsread.glob_truncated': ["count", "total"],
    'web.duckduckgo_blocked': [],
    'web.search_failed': [],
    'web.search_results': ["query", "count", "items"],
    'web.fetch_args_url_required': [],
    'web.js_framework_warning': [],
    'web.low_content_warning': [],
    'web.content_truncated': [],
    'web.body_truncated': ["limit"],
    'web.block_timeout': [],
    'web.block_error': ["error"],
    'web.fetch_all_failed': [],
    'web.no_results': ["query"],
    'web.search_error': ["error"],
    'web.search_rate_limited': [],
    'web.search_auth_failed': [],
    'web.ssrf_blocked': ["reason"],
    'web.too_many_redirects': [],
    'search.no_matches': [],
    'search.matches_truncated': ["shown", "total"],
    'search.rg_not_installed': [],
    'search.error': ["error"],
    'search.path_not_found': ["path"],
    'search.path_not_directory': ["path"],
    'search.invalid_regex': ["error"],
    'plan.enter_success': [],
    'plan.exit_success': [],
    'plan.session_manager_unavailable': ["tool"],
    'plan.enter_failed': [],
    'plan.exit_failed': [],
    'plan.project_store_unavailable': [],
    'plan.no_project': [],
    'plan.no_project_read': [],
    'plan.column_not_found': ["columnId"],
    'plan.item_not_found': ["itemId"],
    'plan.save_error': ["error"],
    'plan.markdown_title': ["version"],
    'plan.markdown_empty': [],
    'plan.markdown_column_empty': [],
    'plan.markdown_dependencies': ["deps"],
    'plan.default_column_backlog': [],
    'plan.default_column_todo': [],
    'plan.default_column_in_progress': [],
    'plan.default_column_done': [],
    'todo.save_error': ["error"],
    'todo.read_error': ["error"],
    'todo.list_empty': [],
    'todo.list': ["count", "items"],
    'todo.item_not_found': ["id"],
    'todo.items_not_found': ["ids"],
    'todo.status_pending': [],
    'todo.status_in_progress': [],
    'todo.status_completed': [],
    'todo.status_cancelled': [],
    'todo.priority_high': [],
    'todo.priority_medium': [],
    'todo.priority_low': [],
    'todo.summary': ["completed", "total", "inProgress"],
    'todo.line': ["mark", "title", "status"],
    'todo.more': ["count"],
    'todo.added': ["ids"],
    'todo.updated': ["id"],
    'todo.deleted': ["count"],
    'todo.hint_priority': ["priority"],
    'todo.hint_plan': ["plan"],
    'project.deps_unavailable': ["tool"],
    'project.session_read_error': [],
    'project.session_not_found': [],
    'project.save_error': ["error"],
    'project.workspace_create_error': ["error"],
    'project.created': ["name", "json"],
    'project.read_error': [],
    'project.not_found': ["id"],
    'project.attached': ["name"],
    'project.log_no_project': [],
    'project.log_save_error': ["error"],
    'project.log_added': ["count"],
    'project.log_empty': [],
    'project.log_list': ["count", "items"],
    'project.invariants_no_project': [],
    'project.invariants_empty': [],
    'project.invariants_saved': [],
    'project.invariants_save_error': ["error"],
    'project.write_forbidden': [],
    'project.updated': [],
    'project.updated_ok': ["name"],
    'project.invalid_icon': ["value", "allowed"],
    'project.invalid_color': ["value", "allowed"],
    'project.no_project': [],
    'project.workspace_set': ["dir"],
    'project.workspace_invalid': [],
    'project.workspace_denied': [],
    'project.workspace_confirm_required': [],
    'project.workspace_missing': [],
    'project.repo_cloned': ["path"],
    'project.repo_initialized': ["path"],
    'project.repo_registered': ["path"],
    'project.repo_fetched': ["path"],
    'project.repo_removed': ["path"],
    'project.repo_not_found': ["path"],
    'projectctx.info_title': ["name"],
    'projectctx.info_id': ["id"],
    'projectctx.info_workspace': ["dir"],
    'projectctx.info_description': ["description"],
    'projectctx.not_found': ["id"],
    'projectctx.missing': [],
    'projectctx.working_dir': ["dir"],
    'projectctx.no_plan_hint': [],
    'projectctx.git_section': [],
    'projectctx.repo_branch': ["branch"],
    'projectctx.repo_remote': ["url"],
    'projectctx.repo_state': ["state"],
    'projectctx.repo_state_dirty': [],
    'projectctx.repo_state_clean': [],
    'projectctx.repo_last_commit': ["commit"],
    'projectctx.repos_title': ["name"],
    'projectctx.repo_not_found': ["path"],
    'projectctx.repos_none': ["dir"],
    'projectctx.repos_found': ["dir"],
    'projectctx.repo_destination': ["dir"],
    'projectctx.repo_name': ["name"],
    'projectctx.repo_url': ["url"],
    'projectctx.repo_type': ["type"],
    'projectctx.repo_default_branch': ["branch"],
    'projectctx.repo_ssh_key': ["id"],
    'projectctx.repo_last_fetch': ["time"],
    'tool.classified': ["category"],
    'reference.session_manager_unavailable': [],
    'reference.session_not_found': ["id"],
    'reference.no_messages': ["id"],
    'reference.session_title': ["title"],
    'permission.yolo_auto_approved': ["reason"],
    'permission.plan_write_blocked': ["source"],
    'permission.plan_source_mode': [],
    'permission.plan_source_strict': [],
    'permission.plan_shell_denied': [],
    'permission.mcp_unavailable': ["mode"],
    'permission.dep_unavailable': ["binary"],
    'permission.workspace_unavailable': [],
    'permission.storage_unavailable': [],
    'permission.external_directory_default': ["path", "action"],
    'permission.no_rules': ["tool"],
    'permission.rule_matched': ["key", "action"],
    'permission.rule_pattern_matched': ["key", "pattern", "action", "path"],
    'permission.rule_prefix_matched': ["key", "action"],
    'permission.rule_command_matched': ["key", "pattern", "action", "command"],
    'permission.ask_timeout': [],
    'permission.ask_cancelled': [],
    'permission.ask_no_subscribers': [],
    'permission.ask_denied': [],
    'permission.authority_denied_unknown': ["action"],
    'permission.authority_denied_explicit': ["action"],
    'permission.authority_denied_no_grant': ["action"],
    'permission.authority_ask': ["action"],
    'shellguard.allow_default': [],
    'shellguard.deny_prefix': ["prefix"],
    'shellguard.deny_path': ["path"],
    'shellguard.ask_path': ["path"],
    'shellguard.deny_dir': ["path", "dir"],
    'protectedpaths.unresolvable': [],
    'protectedpaths.dynamic': [],
    'protectedpaths.allow_default': [],
    'security.warn_prefix': [],
    'security.injection_blocked': ["tool", "matches"],
    'gateway.input_blocked': ["reason"],
    'gateway.rate_limit_warning': [],
    'gateway.output_secrets_reason': [],
    'gateway.filter_denied': ["reason"],
    'gateway.filter_unavailable': ["reason"],
    'router.timeout': [],
    'router.network_error': [],
    'router.provider_not_found': ["provider"],
    'router.provider_disabled': ["provider"],
    'router.provider_unspecified': [],
    'router.model_disabled': ["provider", "model"],
    'router.escalation': ["provider", "model", "reason"],
    'router.context_length_exceeded': [],
    'router.rate_limit_exceeded': [],
    'router.no_user_query': [],
    'qc.min_length_ok': ["length", "min"],
    'qc.min_length_fail_detail': ["min", "length"],
    'qc.min_length_fail': ["min"],
    'qc.constraint_empty': [],
    'qc.constraint_parse_error': ["snippet"],
    'qc.constraint_ok': [],
    'qc.self_check_skipped': [],
    'qc.self_check_unparsed': ["score"],
    'qc.self_check_low': ["score"],
    'qc.self_check_low_reason': ["score"],
    'qc.self_check_ok': ["score"],
    'qc.self_check_error': ["error"],
    'qc.all_passed': [],
    'micro.greeting': [],
    'micro.farewell': [],
    'micro.thanks': [],
    'micro.enum.yes': [],
    'micro.enum.no': [],
    'micro.enum.approve': [],
    'micro.enum.reject': [],
    'micro.enum.mode_plan': [],
    'micro.enum.mode_act': [],
    'micro.intent.question': [],
    'micro.intent.command': [],
    'micro.intent.review': [],
    'micro.intent.explain': [],
    'micro.intent.debug': [],
    'micro.intent.fix': [],
    'micro.intent.test': [],
    // --- Скиллы (ADR-046) ---
    'skill.args_error': ["issues"],
    'skill.not_found': ["name"],
    'skill.untrusted': ["name"],
    'skill.delegation': ["name", "capabilities", "agent"],
    'skill.file_not_found': ["path"],
    'skill.scope_disabled': ["name", "reason"],
    // --- Slash-команды (ADR-046 §7) ---
    'command.unknown': ["name"],
    'command.denied': ["name"],
    'command.unavailable': ["name", "capabilities"],
    'command.help': [],
    'command.list_empty': [],
    'command.skills_empty': [],
    'command.summary_enqueued': [],
    'command.summary_unavailable': [],
    // --- Сжатие контекста (summarization / compaction) ---
    'context.compression_empty': [],
    'context.compression_failed': ["mode", "reason"],
    'context.compression_template': [],
    'context.compression_output_limit': [],
    'context.summary_applied': ["mode"],
    // --- Inline shell (ADR-046 §7.4) ---
    'command.inline_shell_disabled': [],
    'command.inline_shell_denied': ["command", "reason"],
    'command.inline_shell_failed': ["output"],
    'command.inline_shell_truncated': ["limit"],
    'command.inline_shell_unavailable': [],
    // --- Git-репозитории и SSH-ключи (ADR-076) ---
    'sshkey.not_found': ["id"],
    'sshkey.name_exists': ["name"],
    'sshkey.encryption_unavailable': [],
    'sshkey.decrypt_failed': ["id"],
    'sshkey.env_failed': [],
    'git.ssh_key_required': ["path"],
    'git.ssh_key_required_hint': ["path", "keys"],
    'git.ssh_keys_none': [],
    'sshkeys.tool.header': ["count"],
    'sshkeys.tool.empty': [],
    'sshkeys.tool.deps_unavailable': [],
    'git.repo_path_invalid': ["path"],
    'git.repo_not_a_repo': ["path"],
    'git.repo_exists': ["path"],
    // ADR-076: занятый путь различается по состоянию (полноценный репозиторий /
    // след прерванного клона / непустой каталог) — сообщения actionable.
    'git.repo_occupied_repo': ["path"],
    'git.repo_occupied_stub': ["path"],
    'git.repo_occupied_dir': ["path", "entries"],
    'git.repo_path_unreadable': ["path"],
    'git.clone_timeout': ["timeout"],
    'git.fetch_timeout': ["timeout"],
    'git.timeout': ["tool", "timeout"],
    // --- Git worktrees: per-session рабочий каталог (ADR-114) ---
    'worktree.directory_missing': ["path"],
    'worktree.tool.deps_unavailable': [],
    'worktree.tool.no_project': [],
    'worktree.tool.list_empty': [],
    'worktree.tool.list_header': ["count"],
    'worktree.tool.list_row': ["name", "branch", "directory", "dirty", "attached", "exists"],
    'worktree.tool.created': ["name", "branch", "directory", "worktree_id"],
    'worktree.tool.removed': ["name"],
    'worktree.tool.pruned': ["records", "git_pruned"],
    'worktree.tool.force_required': ["name"],
    'worktree.tool.busy': ["name", "session"],
    'worktree.tool.not_found': ["id"],
    // --- Внешние события и хуки (ADR-030 / ADR-089) ---
    'event.ingested': ["id", "source"],
    'event.routed.bound': ["sessionId"],
    'event.routed.ephemeral': ["agentTemplate"],
    'event.routed.queue': ["owner", "reason"],
    'event.routed.drop': ["reason"],
    'event.binding.not_found': ["key"],
    'event.source_not_allowed': ["source"],
    'event.duplicate': ["dedupKey"],
    'hook.blocked': ["hook", "reason"],
    // --- Steering: форсированная отправка во время хода (ADR-104) ---
    'steer.injected': [],
    'steer.limit_exceeded': ["limit"],
    'steer.interrupted': [],
    'steer.dropped_aborted': ["count"],
    'steer.dropped_turn_ended': ["count"],
    'steer.requeued_aborted': ["count"],
    'steer.requeued_turn_ended': ["count"],
    // --- Профиль пользователя и онбординг ---
    'profile.deps_unavailable': ["tool"],
    'profile.session_read_error': [],
    'profile.session_not_found': [],
    'profile.user_unavailable': [],
    'profile.save_error': ["error"],
    'profile.updated': ["fields"],
    // --- Движок пайплайнов (ADR-101) ---
    'pipeline.not_found': ["id"],
    'pipeline.not_published': ["id"],
    'pipeline.invalid_spec': ["problems"],
    'pipeline.loop_limit': ["limit"],
    'pipeline.cancelled': [],
    'pipeline.executor_missing': ["kind"],
    'pipeline.gate_rejected': ["node"],
    'pipeline.gate_timeout': ["node", "timeoutSec"],
    'pipeline.step_failed': ["node", "error"],
    'pipeline.step_timeout': ["node", "timeoutSec"],
    'pipeline.run_not_found': ["id"],
    'pipeline.invalid_state': ["id", "status"],
    'pipeline.tool_not_found': ["tool", "node"],
    'pipeline.tool_failed': ["node", "tool", "error"],
    'pipeline.script_failed': ["node", "code", "error"],
    'pipeline.script_no_command': ["node"],
    'pipeline.permission_denied': ["node", "tool", "reason"],
    'pipeline.agent_unavailable': ["node"],
    'pipeline.provider_not_found': ["node", "provider"],
    'pipeline.llm_failed': ["node", "type"],
    'pipeline.llm_unrecoverable': ["node", "type"],
    // --- Встроенные тулы пайплайнов (ADR-101) ---
    'pipeline.tool.deps_unavailable': ["tool"],
    'pipeline.tool.no_project': [],
    'pipeline.tool.forbidden': [],
    'pipeline.tool.not_found': ["id"],
    'pipeline.tool.already_running': ["id"],
    'pipeline.tool.invalid_spec': ["problems"],
    'pipeline.tool.not_published': ["id"],
    'pipeline.tool.run_not_found': ["id"],
    'pipeline.tool.run_foreign': ["id"],
    'pipeline.tool.run_not_waiting': ["id", "status"],
    'pipeline.tool.template_not_found': ["id"],
    'pipeline.tool.template_unsupported': ["id", "problems"],
    'pipeline.tool.saved': ["id", "version"],
    'pipeline.tool.published': ["id", "version"],
    'pipeline.tool.deleted': ["id"],
    'pipeline.tool.run_started': ["run_id"],
    'pipeline.tool.run_resumed': ["run_id"],
    'pipeline.tool.run_cancelled': ["run_id"],
    'pipeline.tool.instantiated': ["id"],
    'pipeline.tool.list_empty': [],
    'pipeline.tool.runs_empty': [],
    'pipeline.tool.templates_empty': [],
    // --- Встроенные тулы расписаний (ADR-051) ---
    'schedule.tool.deps_unavailable': ["tool"],
    'schedule.tool.forbidden': [],
    'schedule.tool.not_found': ["id"],
    'schedule.tool.invalid': ["problems"],
    'schedule.tool.saved': ["id"],
    'schedule.tool.deleted': ["id"],
    'schedule.tool.ran': ["id", "status"],
    'schedule.tool.list_empty': [],
    'schedule.tool.jobs_empty': [],
    'schedule.tool.target_forbidden': ["reason"],
    'schedule.tool.not_published': ["id"],
    // --- Исполнение (ADR-077) ---
    'execution.remote_url_required': [],
    'execution.remote_unavailable': ["reason"],
    // --- Вопрос пользователю и фоновые процессы (этап 1 roadmap 0.13) ---
    'question.answered': ["answers"],
    'question.cancelled': ["reason"],
    'question.timeout': [],
    'question.deps_unavailable': [],
    'process.background_started': ["id", "log"],
    'process.deps_unavailable': [],
    'process.background_limit': ["limit"],
    'process.background_unsupported': [],
    'process.background_disabled': [],
    'process.detached_started': ["id", "log"],
    'process.tool.empty': [],
    'process.tool.not_found': ["id"],
    'process.tool.deps_unavailable': [],
    'process.tool.header': ["count"],
    'process.tool.no_log': [],
    'process.tool.child_history': ["child_session"],
    'process.tool.not_running': ["id"],
    'process.tool.running': ["id"],
    'process.tool.stopping': ["id"],
    'process.tool.deleted': ["id"],
    'attach.message.from_workspace': ["path"],
    'attach.tool.attached': ["path"],
    'attach.tool.deps_unavailable': [],
    'attach.tool.delivery_unavailable': [],
    'attach.tool.disabled': [],
    'attach.tool.not_found': ["path"],
    'attach.tool.not_a_file': ["path"],
    'attach.tool.too_large': ["path", "size", "limit"],
    'attach.tool.unsupported_mime': ["path", "detail"],
    'attach.tool.modality_unsupported': ["path", "modality"],
    'attach.tool.unrecognized': ["path"],
    'attach.tool.queued': ["name"],
    'attach.tool.saved': ["path", "size"],
    'attach.tool.ambiguous': ["query", "list"],
    'attach.tool.no_attachment': [],
    'attach.tool.exists': ["path"],
    'attach.tool.save_failed': ["error"],
    'models.list.header': ["filter", "shown", "total"],
    'models.list.current': ["model", "modalities"],
    'models.list.defaults': ["assignments"],
    'models.list.skipped': ["count"],
    'models.list.empty': [],
    'models.list.provider_unknown': ["provider"],
    'models.list.deps_unavailable': [],
    'models.list.unavailable': ["reason"],
};
/** Достаёт имена {param}-плейсхолдеров из шаблона. Уникальные, в порядке появления. */
export function extractParams(template) {
    const out = [];
    const seen = new Set();
    const re = /\{([^{}]+)\}/g;
    let m;
    while ((m = re.exec(template)) !== null) {
        const name = m[1].trim();
        if (name && !seen.has(name)) {
            seen.add(name);
            out.push(name);
        }
    }
    return out;
}
